import React, { useState, useEffect, useMemo } from 'react'

import { useSelector } from 'react-redux'

import useStyles from './custom-alert-v1.style'

import ActionButton from '@components/action-button/action-button.component'
import BodyItem from '@components/body-item/body-item.component'
import CardAccordion from '@components/card-accordion/card-accordion.component'
import CardMap from '@components/card-map/card-map.component'
import CarouselContainer from '@components/carousel-container/carousel-container.component'
import UpdatedByContainer from '@components/updated-by-container/updated-by-container.component'

import {
  Card,
  Carousel,
  CardBody,
  CardHeader,
  CardMediaContainer,
  GUN_GRAY,
  Title,
  WHITE,
  Loader
} from '@omnilert/components'

import axios from 'axios'

const CustomAlertV1 = (props) => {
  const [updatedMedia, setUpdatedMedia] = useState([])
  const [isLoadingMedia, setIsLoadingMedia] = useState(true)

  const { message } = props

  const { collection, documentId } = message

  const document = useSelector(({ firestore: { data } }) => data[collection] && data[collection][documentId])

  const {
    actions,
    body,
    collapsable,
    customerActions,
    header,
    location,
    media,
    updatedBy,
    variant
  } = { ...document }

  const titleColor = variant === 'invalid' ? GUN_GRAY : WHITE

  const classes = useStyles({ customerActions, actions, media })

  const hasActions = !!actions?.data?.length

  /*
   * DEV-5189: CPA Firestore docs send `media` as a direct array, but
   * commit 70d5731 switched the carousel to expect a `media.data`
   * wrapper. Normalize both shapes so we tolerate whatever the doc has.
   */
  const mediaItems = useMemo(
    () => (Array.isArray(media) ? media : (media?.data ?? [])),
    [media]
  )

  useEffect(() => {
    let retries = 0
    const maxRetries = 15
    const interval = 1000
    let requestTimeout
    const mediaWithBytes = mediaItems.map((obj) => ({
      ...obj,
      byteSize: 0
    }))

    const pingEndpoint = async () => {
      try {
        const url = mediaWithBytes[0].url || ''

        const response = await axios.head(url)

        const contentLength = response.headers['content-length']

        const byteSize = parseInt(contentLength)

        const cacheBusterUrls = mediaWithBytes.map((obj) => ({
          ...obj,
          byteSize,
          url: `${obj.url}?cacheBuster=${Date.now()}`
        }))

        clearInterval(requestTimeout)
        setUpdatedMedia(cacheBusterUrls)
        setIsLoadingMedia(false)
      } catch (error) {
        if (error.response === undefined) {
          clearInterval(requestTimeout)
          setUpdatedMedia(mediaWithBytes)
          setIsLoadingMedia(false)
          return
        }

        if (!requestTimeout) {
          requestTimeout = setInterval(pingEndpoint, interval)
        }

        retries++

        if (retries === maxRetries) {
          clearInterval(requestTimeout)
          setUpdatedMedia(mediaWithBytes)
          setIsLoadingMedia(false)
        }
      }
    }

    if (mediaItems.length > 0) {
      pingEndpoint()
    } else {
      setIsLoadingMedia(false)
    }
  }, [mediaItems])

  if (!document) return null

  return (
    <Card className={classes.card}>
      {header &&
        <CardHeader
          className={classes.cardHeader}
          type={header.data.variant}
        >
          <Title
            className={classes.title}
            color={titleColor}
            size='sm'
          >{header.data.title}
          </Title>
        </CardHeader>}

      {body && (
        <CardBody className={classes.cardBody}>
          {body.data.map((item, index) => <BodyItem
            key={index}
            item={item}
          />)}
        </CardBody>
      )}

      {isLoadingMedia &&
        <Loader
          className={classes.loader}
          color={WHITE}
          size='lg'
        />}

      {!!updatedMedia.length && (
        <div data-testid='cpa-media-carousel'>
          <CardMediaContainer media={updatedMedia} />
        </div>
      )}

      {collapsable &&
        <div className={classes.accordianContainer}>
          <CardAccordion collapsable={collapsable} />
        </div>}

      {location && <CardMap
        className={classes.map}
        locations={location?.data}
      />}

      {updatedBy && <UpdatedByContainer
        data={updatedBy.data}
        showDivider
      />}

      {(customerActions || hasActions) && (
        <CarouselContainer>
          {customerActions ? (
            <Carousel className={classes.carousel}>
              {customerActions.map(data => (
                <ActionButton
                  key={`action-${data.id}`}
                  data={data}
                  documentId={documentId}
                  pill
                />
              ))}
            </Carousel>
          ) : null}

          {hasActions ? (
            <Carousel
              className={classes.scenariosCarousel}
            >
              {actions.data.map(data => (
                <ActionButton
                  key={`carouselItem-${data.id}`}
                  data={data}
                  documentId={documentId}
                />
              ))}
            </Carousel>
          ) : null}
        </CarouselContainer>
      )}


    </Card>
  )
}

export default CustomAlertV1
